import { useRef, useState, useEffect } from "react";
import * as Sentry from "@sentry/react";
import Layout from "../layouts/Layout.jsx";
import Spinner from "../components/Spinner.jsx";
import DeviceInfo from "../components/DeviceInfo.jsx";
import SparkLine from "../components/SparkLine.jsx";
import Gauge from "../components/Gauge.jsx";
// import { Cog6ToothIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import NoDevices from "../components/NoDevices.jsx";

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [hasFetchedInitialDashboard, setHasFetchedInitialDashboard] = useState(false);
  const sseStartedRef = useRef(false); // Ensures SSE and interval start only once
  const [history, setHistory] = useState(null);
  const apiBaseURL = import.meta.env.VITE_API_BASE_URL;

  const getHistory = async () => {
    try {
      const response = await fetch(apiBaseURL + "history", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (response.status !== 200) {
        //TK setError(await response.text());
        console.error("fetch error ", await response.text());
      } else {
        const jsonResponse = await response.json();
        setHistory(jsonResponse.history);
        console.log("setHistory(", jsonResponse.history);
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error("API fetch error:", error);
      setError(error.message);
    }
  };

  const getDashboard = async () => {
    try {
      const response = await fetch(apiBaseURL + "dashboard", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });

      if (response.status !== 200) {
        setError(await response.text());
        return;
      }

      const dashboardResponse = await response.json();

      if (Object.keys(dashboardResponse).length === 0) {
        setLoading(false);
        return;
      }

      // Process and update dashboard
      let updatedDashboard = {
        ...dashboardResponse,
        devices: Object.fromEntries(
          Object.entries(dashboardResponse.devices || {}).map(([deviceId, deviceData]) => {
            return [
              deviceId,
              {
                ...deviceData,
                sensors: deviceData.sensors?.map(sensor => ({
                  ...sensor,
                  metrics: sensor.metrics.map(metric => {
                    let baseUpdates = {};
                    if (metric.metric === "humidity") {
                      baseUpdates = { min_value: 0, max_value: 100, title: "Humidity %RH" };
                    } else if (metric.metric === "co2") {
                      baseUpdates = { min_value: 0, max_value: 12000, title: "CO2 PPM" };
                    } else {
                      baseUpdates = { min_value: 40, max_value: 100, title: "Temperature °F" };
                    }

                    let thresholdUpdates = {};
                    if (deviceData.thresholds?.thresholds?.[metric.metric]) {
                      thresholdUpdates = {
                        limit_low: deviceData.thresholds.thresholds[metric.metric].low_value,
                        limit_high: deviceData.thresholds.thresholds[metric.metric].high_value,
                      };
                    }

                    return { ...metric, ...baseUpdates, ...thresholdUpdates };
                  }),
                })) || [],
              }
            ];
          })
        )
      };

      const sensor_order = ["orange1", "blue1", "white1"];
      const metric_order = ["temperature", "humidity", "co2"];

      Object.values(updatedDashboard.devices || {}).forEach(device => {
        // Sort sensors within each device
        device.sensors.sort(
          (a, b) => sensor_order.indexOf(a.sensor_code) - sensor_order.indexOf(b.sensor_code)
        );

        // Sort metrics within each sensor
        device.sensors.forEach(sensor => {
          sensor.metrics.sort(
            (a, b) => metric_order.indexOf(a.metric) - metric_order.indexOf(b.metric)
          );
        });
      });

      setDashboard(updatedDashboard);

      if (!hasFetchedInitialDashboard) {
        setHasFetchedInitialDashboard(true);
      }
      setLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      console.error("API fetch error:", error);
      setError(error.message);
    }
  };

  const handleSSEmetrics = (e) => {
    const sseDeviceUpdates = JSON.parse(e.data); // Array of device updates
    setDashboard((prev) => {
      const updatedDashboard = structuredClone(prev);
      sseDeviceUpdates.forEach((deviceUpdate) => {
        const { device_id, uptime, last_seen, sensors } = deviceUpdate;
        const deviceEntry = updatedDashboard.devices[device_id];
        if (deviceEntry) {
          // Update the device's top-level fields
          deviceEntry.device.last_seen = last_seen;
          deviceEntry.device.uptime = uptime;
          
          // Loop over each sensor update from the SSE data
          sensors.forEach((sseSensor) => {
            const sensorToUpdate = deviceEntry.sensors.find(
              (sensor) => sensor.id === sseSensor.id
            );
            if (sensorToUpdate) {
              // Optionally update the sensor's own last_seen if needed
              sensorToUpdate.last_seen = sseSensor.last_seen;
              
              // Update each metric for this sensor
              sseSensor.metrics.forEach((sseMetric) => {
                const metricToUpdate = sensorToUpdate.metrics.find(
                  (metric) => metric.metric === sseMetric.metric
                );
                if (metricToUpdate) {
                  metricToUpdate.latest_value = sseMetric.latest_value;
                  metricToUpdate.last_seen = sseMetric.last_seen;
                }
              });
            }
          });
        }
      });
      return updatedDashboard;
    });
  };

  const startHistoryAndSSE = () => {
    if (dashboard && Object.keys(dashboard).length > 0) {
      getHistory();

      const dashboardEvents = new EventSource(apiBaseURL + "dashboardlive", {
        withCredentials: true,
      });

      dashboardEvents.addEventListener("error", (e) => {
        console.error("SSE error", e);
      });
      dashboardEvents.addEventListener("metrics", handleSSEmetrics);

      const intervalId = setInterval(getHistory, 30000);

      return () => {
        clearInterval(intervalId);
        dashboardEvents.close();
      };
    }
  };


  useEffect(() => {
    getDashboard();
  }, []);

  useEffect(() => {
    if (hasFetchedInitialDashboard && !sseStartedRef.current) {
      sseStartedRef.current = true; // Prevents multiple calls to startHistoryAndSSE
      const cleanup = startHistoryAndSSE();
      return cleanup; // Cleanup for intervals and SSE when component unmounts
    }
  }, [hasFetchedInitialDashboard]);

  if (loading) {
    return (
      <Layout title="Dashboard">
        <div className="container w-full rounded-xl bg-white shadow p-4 h-screen">
          <Spinner />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Dashboard">
        <div className="container w-full rounded-xl bg-white shadow p-4 min-h-40">
          <div>Error loading: {error}</div>
        </div>
      </Layout>
    );
  }

  if (!dashboard || Object.keys(dashboard).length === 0) {
    return (
      <Layout title="Dashboard">
        <div className="container w-full rounded-xl bg-white shadow p-4 min-h-40">
          <NoDevices refreshDashboards={getDashboard} />
        </div>
      </Layout>
    );
  }

  if (!dashboard.devices || dashboard.never_seen) {
    return (
      <Layout title="Dashboard">
        <div className="container w-full rounded-xl bg-white shadow p-4 min-h-40">
          <h3 className="text-cyan-800 text-xl font-semibold py-6 pl-4">
            Your device has never connected to the Fungistics cloud.
            <div className="mt-4 font-medium">Please confirm it is connected to WiFi.</div>
            <div className="mt-4 font-medium">
              Are you stuck? We are
              <Link to="/support" className="text-cyan-500 ml-1 font-bold text-center">
                here to help!
              </Link>
            </div>
          </h3>
        </div>
      </Layout>
    );
  }

  /*
  if (!dashboard.sensors || dashboard.sensors.length < 1) {
    return (
      <Layout title="Dashboard">
        <div className="container w-full rounded-xl bg-white shadow p-4 min-h-40">
          <h3 className="text-cyan-800 text-xl font-semibold py-6 pl-4">
            Your device is connected to the Fungistics cloud, but we have not
            received any sensor data yet...
            <div className="mt-4 font-medium">
              Please confirm you have connected a sensor to the correct port.
            </div>
            <div className="mt-4 font-medium">
              Are you stuck? We are
              <Link to="/support" className="text-cyan-500 ml-1 font-bold text-center">
                here to help!
              </Link>
            </div>
          </h3>
        </div>
      </Layout>
    );
  }
    */

  return (
    <Layout title="Dashboard">
      <div className="container w-full rounded-xl bg-white shadow p-4 min-h-40">
        {dashboard.devices && Object.values(dashboard.devices).map((device) => (
          device.sensors && device.sensors.map((sensor) => (
            <div key={sensor.id}>
              <h3 className="flex justify-between items-center text-cyan-800 text-xl font-semibold py-6 pl-4">
                <div>{sensor.sensor_name}</div>
                <div>
                  <span
                    className={`font-medium pr-4 ${sensor.sensor_code === "orange1" ? "text-orange-500" : "text-cyan-500"
                      }`}
                  >
                    {sensor.sensor_code}
                  </span>
                  <DeviceInfo device={device.device} />
                </div>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 h-100">
                {sensor.metrics.map((metric) => (
                  <div key={metric.id}>
                    <Gauge
                      title={metric.title}
                      value={metric.latest_value ?? 0}
                      limitLow={metric.limit_low}
                      limitHigh={metric.limit_high}
                      minValue={metric.min_value}
                      maxValue={metric.max_value}
                    />
                    {history?.[device.device.id]?.metrics?.[metric.metric]?.[sensor.sensor_code] && (
                      <SparkLine
                        data={history[device.device.id].metrics[metric.metric][sensor.sensor_code]}
                        timespan={history[device.device.id]?.timespan}
                      />
                    )}
                  </div>
                ))}
              </div>
              <hr />
            </div>
          ))
        ))}
      </div>
    </Layout>
  );
}